// HoverCard (round 2): a visit's hover card, on the pop layer (DESIGN.md §1: a frame and a 4 px cast).
// It shows only what the block doesn't (BL-158 9): never the time, who for, the job number or the state
// again, but the address, the work, the arrival window when it isn't the booked time, who else is on it,
// the tags, the site contact, why it's late, who booked it. In labelled fields. It opens after a short
// wait on hover and at once on keyboard focus, and closes on leaving or Escape; it is the block's only
// hover (no native title). It holds no actions, so it has no Close: it goes when the pointer does.
// Reads only --hovercard-*; the layer reads its own.
import type { DOMAttributes, ReactElement } from 'react';
import { Focusable, Tooltip, TooltipTrigger } from 'react-aria-components';
import { KeyValue, type KeyValueItem } from '../KeyValue/KeyValue.tsx';
import { LayerFrame } from '../Layer/Layer.tsx';
import { tokenPx } from '../token-value.ts';
import './HoverCard.css';

/** How long the pointer rests before it opens, and lingers before it goes (ms). */
export const HOVER_OPEN_MS = 450;
export const HOVER_CLOSE_MS = 120;

export interface HoverCardProps {
  /** What it is, for screen readers ("More about the 10–12 visit"). */
  label: string;
  /** What the block doesn't show, in labelled fields. */
  fields: KeyValueItem[];
  /** The block it describes: a VisitBlock (any element that takes a ref and DOM props). */
  children: ReactElement;
  /** For stories and tests: held open. */
  isOpen?: boolean;
  isDisabled?: boolean;
}

export function HoverCard({ label, fields, children, isOpen, isDisabled }: HoverCardProps) {
  if (!fields.length) return children;
  return (
    <TooltipTrigger delay={HOVER_OPEN_MS} closeDelay={HOVER_CLOSE_MS} isOpen={isOpen} isDisabled={isDisabled}>
      {/* the trigger passes Focusable's props and ref on to its button, as a DOM element would */}
      <Focusable>{children as ReactElement<DOMAttributes<HTMLElement>, string>}</Focusable>
      <Tooltip className="fl-hovercard" placement="bottom start" offset={tokenPx('--hovercard-offset')}>
        <LayerFrame level="pop" label={label}>
          <KeyValue items={fields} density="tight" />
        </LayerFrame>
      </Tooltip>
    </TooltipTrigger>
  );
}
